import { MapLike, ValidBusinessObject } from '@iotv/iotv-v3-types'
import { BusinessObjectAssembler } from '../../BusinessObjectAssembler'
import { ArrayLengthLimiter } from '../../model/constructs/AccumulationPeriodization/sharedTypes'
import { DatastoreObjectType } from '../../types'

// this will knowingly over write keys of same name at higher levels !
export const flattenObject = (ob: MapLike<any>, filteredKeys?: string[]): { [k: string]: any } => {
  const map: MapLike<any> = {}

  ob &&
    Object.entries(ob).forEach(([k, v]) => {
      if (v && typeof v === 'object') {
        Object.assign(map, flattenObject(v, filteredKeys))
      } else if (!filteredKeys || filteredKeys.includes(k)) {
        map[k] = v
      }
    })

  return map
}

export const flattenArray = <T>(arr: any[]): T[] =>
  arr.reduce((a, c: T | any[]) => {
    if (c instanceof Array) {
      a = [...a, ...flattenArray(c)]
    } else {
      a = [...a, c as T]
    }
    return a
  }, [])

export const safeJSON = {
  parse: (obstr: string | undefined | null): MapLike<any> | undefined => {
    let res: MapLike<any> | undefined = undefined
    if (typeof obstr === 'string') {
      try {
        res = JSON.parse(obstr)
      } catch (e) {
        console.log('safeJSON caught parse error on probably not JSON string', obstr)
      }
    }

    return res
  },
}

export const removeEmptyArrays = (vob: MapLike<any>) => {
  Object.keys(vob).forEach((k: string) => {
    if (vob[k] instanceof Array && vob[k].length === 0) {
      delete vob.k
    }
  })
}

export const Assembler = new BusinessObjectAssembler()

export const getContainedKeys = (anyObjects: MapLike<any>[]): string[] => {
  const containedKeys: string[] = anyObjects.reduce((acc: string[], curr) => {
    Object.keys(curr).forEach((k) => {
      if (!acc.includes(k)) {
        acc.push(k)
      }
    })
    return acc
  }, [] as string[])

  return containedKeys
}

export const getContainedPopulatedKeys = (anyObjects: MapLike<any>[]): string[] => {
  const containedKeys: string[] = anyObjects.reduce((acc: string[], curr) => {
    Object.entries(curr).forEach(([k, v]) => {
      if ((v || v === 0 || v === false) && !acc.includes(k)) {
        acc.push(k)
      }
    })
    return acc
  }, [] as string[])

  return containedKeys
}

/** updates like Object.assign but ignores null, NaN, undefined, used for example in updating a key that is return only in some device messages  */
export const updateNonNullishValues = <T extends MapLike<any>>(ob: T, update: MapLike<any>) => {
  Object.entries(update).forEach(([k, v]) => {
    if (v || v === 0 || v === false) {
      ;(ob as MapLike<any>)[k] = v
    }
  })
  return ob
}

/**
 * @function smashObjects returns an object with keys as the union of all keys in anyObject and the first defined value for each key
 * This is used to generate ViewDefinitions for untyped, and unpredicable object lists
 * @param anyObjects
 * @returns
 */
export const smashObjects = (anyObjects: MapLike<any>[]): MapLike<any> => {
  let smashed: MapLike<any> = {}
  anyObjects.forEach((o) => {
    Object.entries(o).forEach(([k, v]) => {
      if (!smashed[k] && v !== undefined && v !== null) {
        smashed[k] = v
      }
    })
  })

  return smashed
}

export const getObjectTypeSummary = (anyObjects: MapLike<any>[], key: string = 'type'): MapLike<number> => {
  const res: MapLike<any> = {}
  anyObjects.forEach((object) => {
    if (object[key]) {
      if (!res[object[key]]) {
        res[object[key]] = 1
      } else {
        res[object[key]] = res[object[key]] + 1
      }
    }
  })

  return res
}

/**
 * @function fillWIthX Fills array of n with result of function xFn
 * @param arr
 * @param xFn
 * @returns Array filled with result of xFn
 */
export const fillWIthX = <TA extends (undefined | TF)[], TF>(arr: TA, xFn: () => TF): TF[] => {
  for (let i: number = 0; i < arr.length; i++) {
    arr[i] = xFn()
  }
  return arr as TF[]
}

type NestedArray<T, Depth extends number = 1, TT extends unknown[] = []> = Depth extends TT['length'] ? T : NestedArray<T[], Depth, [unknown, ...TT]>

/**
 * @function fillWithXByArrayDef returns a nested array with lengths as per definition with inner array populated by return of xFn. Array lengths in definition must be valid eg integers
 * depth is arbitrary but limited to 10 from type point of view otherwise compiler hits depth issues
 * @param arrDef
 * @param xFn
 * @returns
 */
export const fillWithXByArrayDef = <T extends number[]>(arrDef: T, xFn: () => any): (() => NestedArray<ReturnType<typeof xFn>, ArrayLengthLimiter<T>>) =>
  arrDef.reduceRight((aFn, cN, i) => () => fillWIthX(new Array(cN), aFn), xFn)
